import React, { useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { actions as sharedActions } from "@shared/store";
import { actions as stationActions, selectors as stationSelectors } from "@containers/Station/store";
import { selectors as hospitalSelectors } from "@containers/Hospital/store";
import { Station } from "@shared/models";
import { OVERLAY_TYPES } from "@shared/constants";
import { CreateStationDto } from "@containers/Station/interfaces";
import { StationForm } from "@containers/Station/components";
import { OverlaySize } from "@shared/interfaces";
import { useStationCreateEditLoading } from "@containers/Station/hooks";

interface StationCreateEditContainerProps {
  onClose: () => void;
  station: Station | null;
}

const StationCreateEditContainer = (props: StationCreateEditContainerProps) => {
  const { onClose, station } = props;
  const dispatch = useDispatch();

  const {
    activateDeactivateStationLoading,
    getStationsConnectionsLoading,
    createStationLoading,
    updateStationLoading,
  } = useStationCreateEditLoading();

  const hospital = useSelector(hospitalSelectors.getHospital());
  const stationsConnections = useSelector(stationSelectors.getStationsConnections());

  const hideOverlayHandler = useCallback(() => {
    dispatch(sharedActions.hideOverlay());
  }, [dispatch]);

  const openStationViewRawDataOverlay = useCallback(
    (station: Station | null) => {
      dispatch(
        sharedActions.showOverlay({
          type: OVERLAY_TYPES.VIEW_STATION_RAW_DATA,
          closeOnOverlayClick: false,
          size: OverlaySize.FULL,
          props: {
            station,
          },
        }),
      );
    },
    [dispatch],
  );

  const handleViewStationRawData = useCallback(
    (station: Station) => {
      openStationViewRawDataOverlay(station);
    },
    [openStationViewRawDataOverlay],
  );

  const handleActivateStation = useCallback(() => {
    if (station) {
      dispatch(
        stationActions.activateDeactivateStation.request({
          id: Number(station.id),
          activate: true,
          cb: () => handleViewStationRawData(station),
        }),
      );
    }
  }, [dispatch, station, handleViewStationRawData]);

  const getStationsConnections = useCallback(
    (stationCode: string) => {
      dispatch(stationActions.getStationsConnections.request({ stations_codes: [stationCode] }));
    },
    [dispatch],
  );

  const changeFormValues = useCallback(() => {
    dispatch(sharedActions.updateOverlay({ leaveConfirm: true }));
  }, [dispatch]);

  const submitForm = useCallback(
    (payload: CreateStationDto) => {
      if (!station) {
        dispatch(stationActions.createStation.request({ ...payload, cb: hideOverlayHandler }));
        return;
      }

      dispatch(stationActions.updateStation.request({ ...payload, id: Number(station.id), cb: hideOverlayHandler }));
    },
    [dispatch, station, hideOverlayHandler],
  );

  useEffect(() => {
    if (station && station.station_code) {
      getStationsConnections(station.station_code);
    }
  }, [getStationsConnections, station]);

  useEffect(() => {
    return () => {
      dispatch(stationActions.getStationsConnections.success(null));
    };
  }, [dispatch]);

  return (
    <div className="station-create-edit-container">
      <StationForm
        isLoading={activateDeactivateStationLoading || createStationLoading || updateStationLoading}
        isConnectionsLoading={getStationsConnectionsLoading}
        stationsConnections={stationsConnections}
        onRefreshConnections={getStationsConnections}
        onChangeForm={changeFormValues}
        onCloseForm={onClose}
        onViewRawData={handleActivateStation}
        onSubmitForm={submitForm}
        station={station}
        hospital={hospital}
      />
    </div>
  );
};

export default StationCreateEditContainer;
